import { isValid, parseISO } from 'date-fns'
import { CalendarFilterCategoryIds } from './categories'
import {
  type CalendarCategoryFilter,
  type CalendarFilterState,
  EMPTY_CALENDAR_FILTERS,
} from './types'
import {
  type CalendarValueTrees,
  impliedFilterMode,
  normalizeCategoryFilter,
} from './valueTree'

/**
 * Applied filters live in the URL so a filtered month can be shared and
 * survives a reload. The names read as `f.<category>.in` and `f.<category>.ex`,
 * with values separated by commas — ids never contain one.
 *
 * `mode` is left out wherever the lists themselves say it, which is almost
 * always: see `MODE_SUFFIX` for the one shape that has to write it down.
 *
 * The live window is the one filter that is not a list of values, so it is
 * written as its two ends instead: see `LIVE_FROM` below.
 *
 * Anything else under `f.` is dropped rather than kept, so an address written
 * before a category was retired — `f.date.start`, `f.type.in` — still opens.
 */
const PREFIX = 'f.'
const INCLUDE_SUFFIX = '.in'
const EXCLUDE_SUFFIX = '.ex'

/**
 * The mode, in the one case the two lists cannot say it.
 *
 * A category with an empty include list hides what it holds, and any other
 * category matches — except one that hides a channel and keeps an account of it
 * back, where both lists have values and the include list is the carve-out. So
 * that shape, and only that shape, writes its mode down.
 */
const MODE_SUFFIX = '.mode'
const EXCLUDE_MODE = 'ex'

/**
 * The live window is two dates rather than a list, so it is written as the two
 * ends it has: `f.live.from` and `f.live.to`.
 */
const LIVE = `${PREFIX}${CalendarFilterCategoryIds.live}`
const LIVE_FROM = `${LIVE}.from`
const LIVE_TO = `${LIVE}.to`

/** ISO 8601 at day precision. Anything else is not a date this can compare. */
const DAY = /^\d{4}-\d{2}-\d{2}$/

/**
 * A day of the window, or nothing where the address does not hold one.
 *
 * The shape alone is not enough: `2026-13-45` is written like a day and is no
 * day at all, and a date nobody can read formats as an invalid one wherever the
 * drawer draws it, which throws and takes the drawer down with it.
 */
const readDay = (params: URLSearchParams, name: string): string | undefined => {
  const value = params.get(name)
  return value !== null && DAY.test(value) && isValid(parseISO(value))
    ? value
    : undefined
}

const isFilterParam = (name: string) => name.startsWith(PREFIX)

const setValues = (
  params: URLSearchParams,
  name: string,
  values: readonly string[]
) => {
  if (values.length > 0) params.set(name, values.join(','))
}

export const serializeCalendarFilters = (
  state: CalendarFilterState
): URLSearchParams => {
  const params = new URLSearchParams()

  Object.entries(state.cats).forEach(([id, category]) => {
    setValues(params, `${PREFIX}${id}${INCLUDE_SUFFIX}`, category.include)
    setValues(params, `${PREFIX}${id}${EXCLUDE_SUFFIX}`, category.exclude)
    if (category.mode === 'exclude' && category.include.length > 0)
      params.set(`${PREFIX}${id}${MODE_SUFFIX}`, EXCLUDE_MODE)
  })

  if (state.live?.from !== undefined) params.set(LIVE_FROM, state.live.from)
  if (state.live?.to !== undefined) params.set(LIVE_TO, state.live.to)

  return params
}

const readValues = (params: URLSearchParams, name: string): string[] =>
  (params.get(name) ?? '').split(',').filter((value) => value.length > 0)

/**
 * Categories that were once written under a name of their own, and the one they
 * are read into now. An address someone saved before Channel and Account became
 * one category still opens on what they saved.
 */
const MERGED_INTO: Readonly<Record<string, string>> = {
  account: CalendarFilterCategoryIds.channels,
  channel: CalendarFilterCategoryIds.channels,
}

export const deserializeCalendarFilters = (
  params: URLSearchParams,
  /**
   * The value trees, where the caller has them. An address is read long before
   * the option lists arrive, and a tree that does not know a value yet would
   * read a carve-out as a stray and drop a filter somebody saved — so without
   * them the two lists are taken as written, and the first edit in a category
   * tidies what is left.
   */
  trees?: CalendarValueTrees
): CalendarFilterState => {
  const cats: Record<string, { exclude: string[]; include: string[] }> = {}
  const read = new Set<string>()

  params.forEach((_value, name) => {
    if (!isFilterParam(name)) return

    const written = name.slice(PREFIX.length).replace(/\.(in|ex|mode)$/, '')
    if (written.length === 0 || read.has(written)) return
    read.add(written)

    const include = readValues(params, `${PREFIX}${written}${INCLUDE_SUFFIX}`)
    const exclude = readValues(params, `${PREFIX}${written}${EXCLUDE_SUFFIX}`)
    if (include.length === 0 && exclude.length === 0) return

    const id = MERGED_INTO[written] ?? written
    const found = cats[id]
    if (found === undefined) cats[id] = { exclude, include }
    else {
      found.include.push(...include)
      found.exclude.push(...exclude)
    }
  })

  const live = {
    from: readDay(params, LIVE_FROM),
    to: readDay(params, LIVE_TO),
  }

  return {
    ...(live.from === undefined && live.to === undefined ? {} : { live }),
    cats: Object.fromEntries(
      Object.entries(cats).map(
        ([id, category]): [string, CalendarCategoryFilter] => {
          const read: CalendarCategoryFilter = {
            // One id written under both a retired name and the one that
            // replaced it is one chosen value, not two: it would otherwise be
            // counted twice on the filter button and drawn as two tokens of one
            // key.
            exclude: [...new Set(category.exclude)],
            include: [...new Set(category.include)],
            // Whatever the address holds beside what the category filters by
            // is a carve-out under it. Only a hidden row with a carve-out
            // writes its mode down.
            mode:
              params.get(`${PREFIX}${id}${MODE_SUFFIX}`) === EXCLUDE_MODE
                ? 'exclude'
                : impliedFilterMode(category),
          }

          const tree = trees?.[id]
          return [
            id,
            tree === undefined ? read : normalizeCategoryFilter(read, tree),
          ]
        }
      )
    ),
  }
}

/**
 * Rewrites only the filter parameters, so whatever else the surface keeps in
 * the URL — the month, a selected item — stays where it is.
 */
export const updateCalendarFilterParams = (
  currentParams: URLSearchParams,
  state: CalendarFilterState = EMPTY_CALENDAR_FILTERS
): URLSearchParams => {
  const params = new URLSearchParams(currentParams)

  Array.from(params.keys())
    .filter(isFilterParam)
    .forEach((name) => params.delete(name))

  serializeCalendarFilters(state).forEach((value, name) => {
    params.set(name, value)
  })

  return params
}
